import React, { useState, useEffect, useRef } from 'react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';
import api from '../../api';

export interface NafCode {
  code: string;
  intitule: string;
}

interface NafCodeSelectorProps {
  selectedNafCodes: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
}

const NafCodeSelector: React.FC<NafCodeSelectorProps> = ({
  selectedNafCodes,
  onChange,
  disabled = false,
  placeholder = "Rechercher un code NAF...",
  label = "Code NAF"
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [nafCodes, setNafCodes] = useState<NafCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fetch NAF codes matching the query
  useEffect(() => {
    const searchNafCodes = async () => {
      if (!query || query.length < 2) return;
      
      setIsLoading(true);
      try {
        const response = await api.get(`/naf/search/${query}`);
        setNafCodes(response.data);
      } catch (error) {
        console.error('Error fetching NAF codes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      searchNafCodes();
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Add click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNafCodeSelect = (code: string) => {
    // Only add if it's not already selected and is a valid string
    if (code && !selectedNafCodes.includes(code)) {
      onChange([...selectedNafCodes, code]);
    }
    
    // Keep dropdown open by preventing default behavior
    setTimeout(() => {
      setIsOpen(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  };

  // Handle button click to toggle dropdown
  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
    if (!isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div ref={containerRef}>
      <label htmlFor="nafCode" className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative mt-1">
        <Combobox 
          value={""} 
          onChange={handleNafCodeSelect} 
          disabled={disabled}
          nullable
        >
          <div className="relative">
            <Combobox.Input
              ref={inputRef}
              id="nafCode"
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              displayValue={() => query}
              autoComplete="off"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none"
              onClick={handleButtonClick}
            >
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </button>
          </div>

          {isOpen && (
            <Combobox.Options static className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {isLoading ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Chargement...
                </div>
              ) : query.length < 2 ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Saisissez au moins 2 caractères pour rechercher...
                </div>
              ) : nafCodes.length === 0 ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Aucun résultat trouvé.
                </div>
              ) : (
                nafCodes.map((nafCode) => (
                  <Combobox.Option
                    key={nafCode.code}
                    value={nafCode.code}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                        active ? 'bg-blue-600 text-white' : 'text-gray-900'
                      }`
                    }
                  >
                    {({ active }) => (
                      <>
                        <div className="flex">
                          <span className={`font-medium ${active ? 'text-white' : 'text-gray-900'}`}>
                            {nafCode.code}
                          </span>
                          <span className={`ml-2 truncate ${active ? 'text-white' : 'text-gray-500'}`}>
                            {nafCode.intitule}
                          </span>
                        </div>

                        {selectedNafCodes.includes(nafCode.code) && (
                          <span
                            className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                              active ? 'text-white' : 'text-blue-600'
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          )}
        </Combobox>
      </div>
    </div>
  );
};

export default NafCodeSelector; 